import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import type { AlarmEntry } from '@/components/dashboard/alarmTypes';
import type { WsAlertPayload } from '@/hooks/useWebSocket';
import { type AlertApiRow, toAlarmEntry } from './types';

interface UseCopAlertsArgs {
  subscribeFullAlert: (cb: (a: WsAlertPayload) => void) => () => void;
  canAckResolve: boolean;
}

/**
 * Whether the alert list is an answer.
 *
 * `loading` until the first poll settles, `failed` once one has, `ok` on any
 * success. An empty list means "nothing is wrong" only under `ok` — before the
 * first response nobody has said, and after a failure the feed is down. Every
 * surface that paints those three states identically is telling an operator
 * all-clear on no evidence, which on an alert surface is the worst thing it can
 * say. Surfaced rather than swallowed, per CLAUDE.md's reliability standard.
 */
export type AlertFeedState = 'loading' | 'ok' | 'failed';

/** Owns the ISA-18.2 alarm list: polling, live merge, acknowledge/resolve. */
export function useCopAlerts({ subscribeFullAlert, canAckResolve }: UseCopAlertsArgs) {
  const [alerts, setAlerts] = useState<AlarmEntry[]>([]);
  const [alertFeedState, setAlertFeedState] = useState<AlertFeedState>('loading');
  /** When the list last came back from a successful poll, as an epoch ms. The
   *  bell shows the age of a frozen reading rather than passing it off as
   *  live. `null` until the first success. */
  const [alertsUpdatedAt, setAlertsUpdatedAt] = useState<number | null>(null);
  const alertRefreshRef = useRef<ReturnType<typeof setInterval>>(undefined);

  const loadAlerts = useCallback(() => {
    // Scoped by status, in two calls, rather than one unfiltered `/api/alerts`.
    //
    // `alertRepo.findByOrg` is `ORDER BY created_at DESC LIMIT 50` and applies
    // no status filter of its own, so an unfiltered fetch spends that window on
    // the 50 newest rows of ANY status — `resolved` included — and the client
    // then discards the resolved ones. An org that churns alerts therefore
    // pushed its OLDEST STANDING alerts out of the payload: the bell painted
    // "no active alerts" while a critical stood unacknowledged, which is the
    // one thing an alert surface must never say. The oldest standing alert is
    // also the one most likely to have been forgotten.
    //
    // Scoped, a standing alert can only be crowded out by 50 other alerts in
    // the same state — in which case the bell reads 50 and is shouting, not
    // claiming all-clear. Above 50 simultaneously active alerts the count
    // under-reports; that is an alert-flood accuracy limit, not a false
    // all-clear, and closing it needs the server's page size raised.
    Promise.all([
      api.get<{ data: AlertApiRow[] }>('/api/alerts?status=active'),
      api.get<{ data: AlertApiRow[] }>('/api/alerts?status=acknowledged'),
    ])
      .then(([active, acknowledged]) => {
        setAlerts([...active.data, ...acknowledged.data].map(toAlarmEntry));
        setAlertFeedState('ok');
        setAlertsUpdatedAt(Date.now());
      })
      .catch(() => {
        console.warn('Failed to load alerts for alarm panel');
        setAlertFeedState('failed');
      });
  }, []);

  useEffect(() => {
    loadAlerts();
    alertRefreshRef.current = setInterval(loadAlerts, 30_000);
    return () => clearInterval(alertRefreshRef.current);
  }, [loadAlerts]);

  const handleLiveAlert = useCallback((payload: WsAlertPayload) => {
    const entry = toAlarmEntry(payload as AlertApiRow);
    setAlerts((prev) => {
      const exists = prev.find((a) => a.id === entry.id);
      if (exists) {
        return prev.map((a) => (a.id === entry.id ? entry : a));
      }
      return [entry, ...prev];
    });
  }, []);

  // Subscribes to live WebSocket alerts and returns the unsubscribe cleanup.
  // This is external-store subscription, not lifting data to a parent:
  // `subscribeFullAlert` is a subscribe fn and `handleLiveAlert` updates this
  // hook's own state. Flagged as a heuristic false positive.
  // react-doctor-disable-next-line react-doctor/no-pass-data-to-parent
  useEffect(() => subscribeFullAlert(handleLiveAlert), [subscribeFullAlert, handleLiveAlert]);

  const handleAcknowledge = useCallback((id: string) => {
    if (!canAckResolve) return;
    api
      .post<{ data: AlertApiRow }>(`/api/alerts/${id}/acknowledge`, {})
      .then((r) => {
        const updated = toAlarmEntry(r.data);
        setAlerts((prev) => prev.map((a) => (a.id === id ? updated : a)));
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to acknowledge'));
      });
  }, [canAckResolve]);

  const handleResolve = useCallback((id: string) => {
    if (!canAckResolve) return;
    api
      .post<{ data: AlertApiRow }>(`/api/alerts/${id}/resolve`, {})
      .then((r) => {
        const updated = toAlarmEntry(r.data);
        setAlerts((prev) => prev.map((a) => (a.id === id ? updated : a)));
      })
      .catch((err: unknown) => {
        toast.error(getApiErrorMessage(err, 'Failed to resolve'));
      });
  }, [canAckResolve]);

  return { alerts, alertFeedState, alertsUpdatedAt, handleAcknowledge, handleResolve };
}
